/**
 * Reply Preview Component
 * 
 * Displays a preview of the message being replied to above the reply message.
 */

import React from 'react';
import { Typography } from 'antd';
import styled from '@emotion/styled';
import type { Message } from '@municipality/types';
import DOMPurify from 'dompurify';
import { useResponsive } from '@municipality/ui';

const { Text } = Typography;

interface ReplyPreviewProps {
  repliedToMessage: Message;
  participantDetails: Record<string, { name: string; avatar?: string }>;
  onClick?: () => void;
}

const PreviewContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${({ $isMobile }) => ($isMobile ? '6px 10px' : '8px 12px')};
  margin-bottom: ${({ $isMobile }) => ($isMobile ? '6px' : '8px')};
  border-left: 3px solid #1890ff;
  background-color: #f5f5f5;
  border-radius: 4px;
  cursor: ${(props) => (props.onClick ? 'pointer' : 'default')};
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${(props) => (props.onClick ? '#e6f7ff' : '#f5f5f5')};
  }
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const PreviewSenderName = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  color: #1890ff;
`;

const PreviewContent = styled(Text)`
  font-size: 12px;
  color: #666;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
`;

/**
 * Sanitize message content to prevent XSS attacks
 */
const sanitizeContent = (content: string): string => {
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  });
};

export const ReplyPreview: React.FC<ReplyPreviewProps> = ({
  repliedToMessage,
  participantDetails,
  onClick,
}) => {
  const { isMobile } = useResponsive();
  const senderName = participantDetails[repliedToMessage.senderId]?.name || repliedToMessage.senderName;
  const isDeleted = repliedToMessage.deletedBy.length > 0;
  const sanitizedContent = sanitizeContent(repliedToMessage.content);
  const previewContent = isDeleted ? '[Message deleted]' : sanitizedContent;

  return (
    <PreviewContainer onClick={onClick} $isMobile={isMobile}>
      <PreviewHeader>
        <PreviewSenderName>{senderName}</PreviewSenderName>
      </PreviewHeader>
      <PreviewContent>{previewContent}</PreviewContent>
    </PreviewContainer>
  );
};
